import { requireDeviceSku } from '../state/deviceCatalog';
import {
  type CaseShowcaseRecord,
  createCaseShowcaseFixture,
} from './caseShowcase';
import {
  readCaseShowcaseMockCases,
  readCaseShowcaseMockPlanRelations,
} from './caseShowcaseSession';

export const CASE_SHOWCASE_SHARE_TOKEN_PREFIX = 'CASEMOCK_';

const MAX_CASES = 24;
const MAX_ITEMS_PER_CASE = 36;
const MAX_ITEMS_TOTAL = 144;
const MAX_TOKEN_LENGTH = 48_000;

interface CaseShowcaseSharePayloadV1 {
  v: 1;
  c: string;
  p: string;
  e: string;
  i: Array<[deviceId: string, quantity: number, unitPrice: number]>;
}

interface CaseShowcaseCartShareItemPayload {
  deviceId: string;
  name: string;
  sku: string;
  image: string;
  quantity: number;
  unitPrice: number;
}

interface CaseShowcaseCartShareCasePayload {
  caseId: string;
  caseCode: string;
  caseVersion: number;
  name: string;
  summary: string;
  heroImage: string;
  planId: string;
  planName: string;
  items: CaseShowcaseCartShareItemPayload[];
}

interface CaseShowcaseSharePayloadV2 {
  v: 2;
  e: string;
  cases: CaseShowcaseCartShareCasePayload[];
}

export interface CaseShowcaseShareInput {
  caseId: string;
  planId: string;
  items: Array<{
    deviceId: string;
    quantity: number;
    unitPrice: number;
  }>;
}

export interface CaseShowcaseCartShareInput {
  cases: CaseShowcaseShareInput[];
}

export interface CaseShowcaseShareCaseSnapshot {
  quoteNo: string;
  case: {
    caseId: string;
    caseCode: string;
    caseVersion: number;
    name: string;
    summary: string;
    heroImage: string;
  };
  plan: {
    id: string;
    name: string;
  };
  items: CaseShowcaseCartShareItemPayload[];
}

export interface CaseShowcaseShareSnapshot {
  token: string;
  shareType: 'case' | 'cart';
  quoteNo: string;
  quoteVersion: 1;
  validUntil: string;
  /** Top-level fields keep the original single-case share contract available. */
  case: CaseShowcaseShareCaseSnapshot['case'];
  plan: CaseShowcaseShareCaseSnapshot['plan'];
  items: CaseShowcaseShareCaseSnapshot['items'];
  /** A v1 link exposes one case here; a v2 cart link exposes every case. */
  cases: CaseShowcaseShareCaseSnapshot[];
}

function encodePayload(
  payload: CaseShowcaseSharePayloadV1 | CaseShowcaseSharePayloadV2,
): string {
  const bytes = new TextEncoder().encode(JSON.stringify(payload));
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary)
    .replaceAll('+', '-')
    .replaceAll('/', '_')
    .replace(/=+$/, '');
}

function decodePayload(value: string): unknown {
  if (!/^[A-Za-z0-9_-]+$/.test(value))
    throw new Error('invalid token encoding');
  const standard = value.replaceAll('-', '+').replaceAll('_', '/');
  const padded = `${standard}${'='.repeat((4 - (standard.length % 4)) % 4)}`;
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isDate(value: unknown): value is string {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false;
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  return (
    !Number.isNaN(date.valueOf()) && date.toISOString().slice(0, 10) === value
  );
}

function isCaseId(value: unknown): value is string {
  return typeof value === 'string' && /^CASE-\d{3}$/.test(value);
}

function isPlanId(value: unknown): value is string {
  return typeof value === 'string' && /^PLAN-\d{3}$/.test(value);
}

function isDeviceId(value: unknown): value is string {
  return typeof value === 'string' && /^DEV-\d{3}$/.test(value);
}

function isText(value: unknown, maxLength: number): value is string {
  return (
    typeof value === 'string' &&
    value.trim().length > 0 &&
    value.length <= maxLength
  );
}

function isSafeImageSource(value: unknown): value is string {
  return (
    isText(value, 4_096) &&
    (value.startsWith('data:image/svg+xml;utf8,') ||
      /^\/(?!\/)[^\\]*$/.test(value))
  );
}

/** V2 snapshots may carry a bounded local upload; remote hosts and SVG base64 remain excluded. */
function isSafeCaseImageSource(value: unknown): value is string {
  return (
    isSafeImageSource(value) ||
    (isText(value, MAX_TOKEN_LENGTH) &&
      /^data:image\/(?:jpeg|png|webp|gif);base64,[A-Za-z0-9+/]+={0,2}$/.test(
        value,
      ))
  );
}

function isQuantity(value: unknown): value is number {
  return Number.isInteger(value) && Number(value) >= 1 && Number(value) <= 999;
}

function isPrice(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isFinite(value) &&
    value >= 0 &&
    value <= 10_000_000
  );
}

function isLegacyPayload(value: unknown): value is CaseShowcaseSharePayloadV1 {
  if (!isRecord(value)) return false;
  return (
    value.v === 1 &&
    isCaseId(value.c) &&
    isPlanId(value.p) &&
    isDate(value.e) &&
    Array.isArray(value.i) &&
    value.i.length > 0 &&
    value.i.length <= MAX_ITEMS_PER_CASE &&
    value.i.every(
      (item) =>
        Array.isArray(item) &&
        item.length === 3 &&
        isDeviceId(item[0]) &&
        isQuantity(item[1]) &&
        isPrice(item[2]),
    )
  );
}

function isCartCasePayload(
  value: unknown,
): value is CaseShowcaseCartShareCasePayload {
  if (!isRecord(value) || !Array.isArray(value.items)) return false;
  if (
    !isCaseId(value.caseId) ||
    !isText(value.caseCode, 80) ||
    !Number.isInteger(value.caseVersion) ||
    Number(value.caseVersion) < 1 ||
    !isText(value.name, 160) ||
    typeof value.summary !== 'string' ||
    value.summary.length > 1_000 ||
    !isSafeCaseImageSource(value.heroImage) ||
    !isPlanId(value.planId) ||
    !isText(value.planName, 160) ||
    value.items.length > MAX_ITEMS_PER_CASE
  ) {
    return false;
  }

  const seen = new Set<string>();
  return value.items.every((item) => {
    if (
      !isRecord(item) ||
      !isDeviceId(item.deviceId) ||
      !isText(item.name, 160) ||
      !isText(item.sku, 120) ||
      !isSafeImageSource(item.image) ||
      !isQuantity(item.quantity) ||
      !isPrice(item.unitPrice) ||
      seen.has(item.deviceId)
    ) {
      return false;
    }
    seen.add(item.deviceId);
    return true;
  });
}

function isCartPayload(value: unknown): value is CaseShowcaseSharePayloadV2 {
  if (
    !isRecord(value) ||
    value.v !== 2 ||
    !isDate(value.e) ||
    !Array.isArray(value.cases) ||
    value.cases.length === 0 ||
    value.cases.length > MAX_CASES
  ) {
    return false;
  }
  const seen = new Set<string>();
  let totalItems = 0;
  return value.cases.every((casePayload) => {
    if (!isCartCasePayload(casePayload) || seen.has(casePayload.caseId)) {
      return false;
    }
    seen.add(casePayload.caseId);
    totalItems += casePayload.items.length;
    return totalItems <= MAX_ITEMS_TOTAL;
  });
}

function validUntilFrom(now: Date): string {
  const validUntil = new Date(now);
  validUntil.setUTCDate(validUntil.getUTCDate() + 14);
  return validUntil.toISOString().slice(0, 10);
}

function shareCaseSnapshot(
  caseRecord: CaseShowcaseRecord,
  planId: string,
  planName: string,
  items: CaseShowcaseShareCaseSnapshot['items'],
): CaseShowcaseShareCaseSnapshot {
  return {
    quoteNo: `CASE-MOCK-${caseRecord.caseId}-${planId}`,
    case: {
      caseId: caseRecord.caseId,
      caseCode: caseRecord.caseCode,
      caseVersion: caseRecord.version,
      name: caseRecord.name,
      summary: caseRecord.summary,
      heroImage: caseRecord.heroImage,
    },
    plan: { id: planId, name: planName },
    items,
  };
}

function snapshotFromCases(
  token: string,
  shareType: CaseShowcaseShareSnapshot['shareType'],
  validUntil: string,
  cases: CaseShowcaseShareCaseSnapshot[],
): CaseShowcaseShareSnapshot {
  const firstCase = cases[0];
  return {
    token,
    shareType,
    quoteNo:
      shareType === 'cart'
        ? `CASE-MOCK-CART-${cases.length}`
        : firstCase.quoteNo,
    quoteVersion: 1,
    validUntil,
    case: firstCase.case,
    plan: firstCase.plan,
    items: firstCase.items,
    cases,
  };
}

function validInputItems(items: CaseShowcaseShareInput['items']): boolean {
  if (!Array.isArray(items) || items.length > MAX_ITEMS_PER_CASE) {
    return false;
  }
  const seen = new Set<string>();
  return items.every((item) => {
    if (
      !item ||
      !isDeviceId(item.deviceId) ||
      !isQuantity(item.quantity) ||
      !isPrice(item.unitPrice) ||
      seen.has(item.deviceId)
    ) {
      return false;
    }
    seen.add(item.deviceId);
    return true;
  });
}

export function createCaseShowcaseShareToken(
  input: CaseShowcaseShareInput,
  now = new Date(),
): string {
  const payload: CaseShowcaseSharePayloadV1 = {
    v: 1,
    c: input.caseId,
    p: input.planId,
    e: validUntilFrom(now),
    i: input.items.map((item) => [
      item.deviceId,
      item.quantity,
      item.unitPrice,
    ]),
  };
  return `${CASE_SHOWCASE_SHARE_TOKEN_PREFIX}${encodePayload(payload)}`;
}

export function createCaseShowcaseCartShareToken(
  input: CaseShowcaseCartShareInput,
  now = new Date(),
): string {
  if (
    !input ||
    !Array.isArray(input.cases) ||
    input.cases.length === 0 ||
    input.cases.length > MAX_CASES
  ) {
    throw new Error('购物车案例数量不符合分享要求');
  }

  const fixture = createCaseShowcaseFixture();
  const savedCases = readCaseShowcaseMockCases(fixture);
  const casePlanRelations = readCaseShowcaseMockPlanRelations(fixture);
  const seenCases = new Set<string>();
  let totalItems = 0;
  const cases = input.cases.map((entry) => {
    if (
      !entry ||
      !isCaseId(entry.caseId) ||
      !isPlanId(entry.planId) ||
      seenCases.has(entry.caseId) ||
      !validInputItems(entry.items)
    ) {
      throw new Error('购物车中存在无法分享的案例或设备明细');
    }
    seenCases.add(entry.caseId);
    totalItems += entry.items.length;
    if (totalItems > MAX_ITEMS_TOTAL) {
      throw new Error('购物车设备明细过多，暂时无法生成分享链接');
    }

    const caseRecord = savedCases.find(
      (record) => record.caseId === entry.caseId,
    );
    const plan = fixture.catalog.plans.find(
      (record) => record.id === entry.planId,
    );
    if (
      !caseRecord ||
      !plan ||
      !(casePlanRelations[caseRecord.id] ?? caseRecord.planIds).includes(
        plan.id,
      )
    ) {
      throw new Error('购物车案例或所选方案已不可用');
    }

    const items = entry.items.map((item) => {
      const device = fixture.catalog.devices.find(
        (record) => record.id === item.deviceId,
      );
      if (!device) throw new Error('购物车包含无法识别的设备');
      return {
        deviceId: device.id,
        name: device.name,
        sku: requireDeviceSku(device),
        image: device.image,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
      };
    });

    return {
      caseId: caseRecord.caseId,
      caseCode: caseRecord.caseCode,
      caseVersion: caseRecord.version,
      name: caseRecord.name,
      summary: caseRecord.summary,
      heroImage: caseRecord.heroImage,
      planId: plan.id,
      planName: plan.name,
      items,
    } satisfies CaseShowcaseCartShareCasePayload;
  });

  const payload: CaseShowcaseSharePayloadV2 = {
    v: 2,
    e: validUntilFrom(now),
    cases,
  };
  if (!isCartPayload(payload)) {
    throw new Error('案例快照不符合分享要求，请检查案例字段和效果图');
  }
  const token = `${CASE_SHOWCASE_SHARE_TOKEN_PREFIX}${encodePayload(payload)}`;
  if (token.length > MAX_TOKEN_LENGTH) {
    throw new Error('购物车快照过大，暂时无法生成分享链接');
  }
  return token;
}

/**
 * undefined means this belongs to the formal public-share route; null means a
 * CASEMOCK token was requested but failed the self-contained fixture contract.
 */
export function readCaseShowcaseShareToken(
  token: string,
  now = new Date(),
): CaseShowcaseShareSnapshot | null | undefined {
  if (!token.startsWith(CASE_SHOWCASE_SHARE_TOKEN_PREFIX)) return undefined;
  if (token.length > MAX_TOKEN_LENGTH) return null;
  try {
    const decoded = decodePayload(
      token.slice(CASE_SHOWCASE_SHARE_TOKEN_PREFIX.length),
    );
    if (isLegacyPayload(decoded)) {
      if (decoded.e < now.toISOString().slice(0, 10)) return null;
      const fixture = createCaseShowcaseFixture();
      const caseRecord = fixture.cases.find(
        (record) => record.caseId === decoded.c,
      );
      const plan = fixture.catalog.plans.find(
        (record) => record.id === decoded.p,
      );
      if (!caseRecord || !plan) return null;

      const seen = new Set<string>();
      const items = decoded.i.map(([deviceId, quantity, unitPrice]) => {
        if (seen.has(deviceId)) throw new Error('duplicate mock share item');
        seen.add(deviceId);
        const device = fixture.catalog.devices.find(
          (record) => record.id === deviceId,
        );
        if (!device) throw new Error('unknown mock share item');
        return {
          deviceId,
          name: device.name,
          sku: requireDeviceSku(device),
          image: device.image,
          quantity,
          unitPrice,
        };
      });
      const caseSnapshot = shareCaseSnapshot(
        caseRecord,
        plan.id,
        plan.name,
        items,
      );
      return snapshotFromCases(token, 'case', decoded.e, [caseSnapshot]);
    }

    if (!isCartPayload(decoded) || decoded.e < now.toISOString().slice(0, 10)) {
      return null;
    }
    const cases = decoded.cases.map((casePayload) => ({
      quoteNo: `CASE-MOCK-${casePayload.caseId}-${casePayload.planId}`,
      case: {
        caseId: casePayload.caseId,
        caseCode: casePayload.caseCode,
        caseVersion: casePayload.caseVersion,
        name: casePayload.name,
        summary: casePayload.summary,
        heroImage: casePayload.heroImage,
      },
      plan: { id: casePayload.planId, name: casePayload.planName },
      items: casePayload.items,
    }));
    return snapshotFromCases(token, 'cart', decoded.e, cases);
  } catch {
    return null;
  }
}
